import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCustomerAnalytics } from "../../../hooks/useAnalytics";
import { AnalyticsFilter } from "../../../types/analytics";
import PeriodSelector from "../../../components/backend/analytics/PeriodSelector";
import RevenueChart from "../../../components/backend/analytics/RevenueChart";
import PieBreakdown from "../../../components/backend/analytics/PieBreakdown";
import KpiStatCard from "../../../components/backend/analytics/KpiStatCard";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RepeatIcon from "@mui/icons-material/Repeat";

const CustomerInsightsPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AnalyticsFilter>({ period: "30d" });
  const { data: response, isLoading } = useCustomerAnalytics(filter);
  const customers = response?.data;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight={700}>
          {t("admin.analytics.customerInsights")}
        </Typography>
        <PeriodSelector filter={filter} onChange={setFilter} />
      </Box>

      {customers && (
        <>
          {/* KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiStatCard
                title={t("admin.analytics.totalCustomers")}
                data={{
                  currentValue: customers.totalCustomers,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                icon={<PeopleIcon />}
                color="#6366f1"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiStatCard
                title={t("admin.analytics.newCustomers")}
                data={{
                  currentValue: customers.newCustomersInPeriod,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                icon={<PersonAddIcon />}
                color="#10b981"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiStatCard
                title={t("admin.analytics.returningCustomers")}
                data={{
                  currentValue: customers.returningCustomers,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                icon={<RepeatIcon />}
                color="#f59e0b"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiStatCard
                title={t("admin.analytics.avgSpendPerCustomer")}
                data={{
                  currentValue: customers.averageSpendPerCustomer,
                  previousValue: 0,
                  changePercentage: 0,
                  changeDirection: "flat",
                }}
                prefix="$"
                color="#ec4899"
              />
            </Grid>
          </Grid>

          {/* New Customer Timeline + Auth Type Pie */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <RevenueChart
                title={t("admin.analytics.newCustomers")}
                revenueData={customers.newCustomerTimeline}
                height={300}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PieBreakdown
                title={t("admin.analytics.customersByAuthType")}
                data={customers.customersByAuthType}
                showValue={false}
              />
            </Grid>
          </Grid>

          {/* Top Customers Table */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t("admin.analytics.topCustomers")}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.dashboard.customer")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {t("admin.analytics.orderCount")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {t("admin.analytics.totalRevenue")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.dashboard.date")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.topCustomers.map((customer, index) => (
                      <TableRow key={customer.userId}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: 11,
                                background:
                                  "linear-gradient(135deg, #6366f1, #818cf8)",
                              }}
                            >
                              {customer.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>
                              {customer.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {customer.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {customer.orderCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="success.main"
                          >
                            ${customer.totalSpent.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {customer.lastOrderDate
                              ? new Date(
                                  customer.lastOrderDate
                                ).toLocaleDateString()
                              : "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default CustomerInsightsPage;
